<template>
<!-- eslint-disable max-len -->
<span>
  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <div v-if="showDescription"
      class="backstory-view"
      :class="{ native }"
      :style="{ height: backstoryHeight }"
    >
      <template v-if="backstories.length > 1">
        <transition enter-active-class="animated fadeIn">
          <button id="left-select" class="btn select" @click.prevent.stop="prevBackstory">
            <img :src="resolveAssetPath('ArrowL_Tall.png', 'ui/customer')">
          </button>
        </transition>
      </template>
      <div style="overflow: hidden; width: 970px;" ref="backstoryWrapper">
        <transition :enter-active-class="enterActiveClass" :leave-active-class="leaveActiveClass">
          <div v-if="showCurrentBackstory">
            <!--
              Never use v-html with end-user generated content, because it's a vector for XSS attacks
            -->
            <h1 v-if="!(game.backstorySelectNoHeaders)" v-html="selectedBackstory.title"></h1>

            <!--
              Never use v-html with end-user generated content, because it's a vector for XSS attacks
            -->
            <p class="backstory-description" v-html="selectedBackstory.description"></p>
          </div>
        </transition>
      </div>
      <template v-if="backstories.length > 1">
        <transition enter-active-class="animated fadeIn">
          <button id="right-select" class="btn select" @click.prevent.stop="nextBackstory" v-focus autofocus>
            <img :src="resolveAssetPath('ArrowL_Tall.png', 'ui/customer')">
          </button>
        </transition>
      </template>
    </div>
  </transition>

  <div class="backstory-prompt-container">
    <div class="backstory-prompt" v-html="game.backstorySelectPromptText">
    </div>
    <transition enter-active-class="animated fadeIn">
      <button
        v-if="showButton"
        class="btn backstory-submit-button"
        @click.prevent.stop="navigateAndSaveSelection"
      >
        <span v-html="(game && game.backstorySelectContinueButtonText) ? game.backstorySelectContinueButtonText : ((game && game.nextButtonDefaultText) ? game.nextButtonDefaultText : 'Next')"></span>
      </button>
    </transition>
  </div>

  <transition enter-active-class="animated slideInLeft" leave-active-class="animated fadeOut">
    <player v-if="showNarrator && game.backstorySelectAfterAvatarSelect && !(game.backstorySelectUsesNarrator)"
      class="backstory-avatar"
      :class="{ native }"
      :variantOverride="game.backstorySelectNarratorVariant"
      :xOffset="game.backstorySelectNarratorXOffset"
      :yOffset="game.backstorySelectNarratorYOffset"
    ></player>
    <narrator v-else-if="showNarrator"
      class="backstory-avatar"
      :class="{ native }"
      :variantOverride="game.backstorySelectNarratorVariant"
      :xOffset="game.backstorySelectNarratorXOffset"
      :yOffset="game.backstorySelectNarratorYOffset"
      left
    ></narrator>
  </transition>
</span>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';

import Narrator from '../ui/Narrator';
import Player from '../ui/Player';

export default {
  components: {
    Narrator,
    Player,
  },
  data() {
    return {
      showNarrator: false,
      showTitle: false,
      showDescription: false,
      showButtons: false,
      showButton: false,
      selectedBackstoryIndex: 0,
      shuffledIndices: [],
      showCurrentBackstory: true,
      backstoryHeight: '250px',
      enterActiveClass: 'animated fadeInRight',
      leaveActiveClass: 'animated fadeOutLeft',
      errorCount: 0,
      maxErrors: 3,
      submitting: false,
      intervalID: null,
      failCount: 0,
    };
  },
  computed: {
    ...mapGetters([
      'group',
      'game',
      'activeClass',
      'challenge',
      'starfighter',
      'native',
    ]),
    backstories() {
      return ((this.game.backstories && this.game.backstories.length)
        ? ((this.activeClass && this.activeClass.randomChoiceOrder)
          ? this.shuffledBackstories
          : this.game.backstories)
        : []);
    },
    shuffledBackstories() {
      if (this.game.backstories && this.game.backstories.length) {
        if (this.shuffledIndices && this.shuffledIndices.length
          && this.shuffledIndices.length === this.game.backstories.length) {
          return this.game.backstories.map(
            (ch, index) => this.game.backstories[this.shuffledIndices[index]]
          );
        }
        return this.shuffle(this.game.backstories);
      }
      return [];
    },
    selectedBackstory() {
      return ((this.backstories && this.backstories.length)
        ? (this.backstories[this.selectedBackstoryIndex] || {})
        : {});
    },
  },
  methods: {
    ...mapActions([
      'stopSounds',
      'selectBackstory',
      'playSound',
    ]),
    async playArrowButtonSound() {
      const sound = this.game.arrowButtonSound;
      if (sound) {
        await Vue.nextTick();
        this.playSound({ sound });
      }
    },
    async nextBackstory() {
      this.playArrowButtonSound();
      this.enterActiveClass = 'animated fadeInRight';
      this.leaveActiveClass = 'animated fadeOutLeft';
      await Vue.nextTick();
      this.showCurrentBackstory = false; // animate out
      await new Promise(resolve => setTimeout(resolve, 300));
      this.selectedBackstoryIndex = (this.selectedBackstoryIndex + 1) % this.backstories.length;
      this.showCurrentBackstory = true; // animate in
      await Vue.nextTick();
      this.backstoryHeight = this.backstoryWrapperHeight();
    },
    async prevBackstory() {
      this.playArrowButtonSound();
      this.enterActiveClass = 'animated fadeInLeft';
      this.leaveActiveClass = 'animated fadeOutRight';
      await Vue.nextTick();
      this.showCurrentBackstory = false; // animate out
      await new Promise(resolve => setTimeout(resolve, 300));
      this.selectedBackstoryIndex -= 1;
      if (this.selectedBackstoryIndex < 0) this.selectedBackstoryIndex = this.backstories.length - 1;
      this.showCurrentBackstory = true; // animate in
      await Vue.nextTick();
      this.backstoryHeight = this.backstoryWrapperHeight();
    },
    backstoryWrapperHeight() {
      return (this.$refs.backstoryWrapper && this.$refs.backstoryWrapper.clientHeight)
        ? `${this.$refs.backstoryWrapper.clientHeight}px`
        : 'auto';
    },
    async navigateAndSaveSelection() {
      if (this.submitting) return;
      this.submitting = true;

      try {
        await this.selectBackstory({ backstory: this.selectedBackstory });
        await this.hide();
        this.$router.nextGameState();
      } catch (err) {
        if (await this.$router.handleGameError(err)) return;
        this.errorCount += 1;
        if (this.errorCount >= this.maxErrors) return this.$router.showGameErrorCritical();
        // TODO: flash message ?
        this.submitting = false;
      }
    },
    async show() {
      this.showNarrator = true;
      await Vue.nextTick();
      this.stopSounds();
      await new Promise(resolve => setTimeout(resolve, 1250));
      this.showTitle = true;
      this.showDescription = true;
      this.showCurrentBackstory = true;
      await Vue.nextTick();
      this.backstoryHeight = this.backstoryWrapperHeight();
      await new Promise(resolve => setTimeout(resolve, 1250));
      this.showButton = true;
      await Vue.nextTick();
      this.showButtons = true;
    },
    async hide() {
      this.showButtons = false;
      this.showButton = false;
      this.showDescription = false;
      await new Promise(resolve => setTimeout(resolve, 500));
      this.showNarrator = false;
      await new Promise(resolve => setTimeout(resolve, 500));
      this.stopSounds();
    },
    shuffle(array) {
      // ***
      // Fisher-Yates JS implementation from: https://stackoverflow.com/a/2450976
      // ***
      let currentIndex = array.length;
      let temporaryValue;
      let randomIndex;
      // While there remain elements to shuffle...
      while (currentIndex !== 0) {
        // Pick a remaining element...
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex -= 1;
        // And swap it with the current element.
        temporaryValue = array[currentIndex];
        array[currentIndex] = array[randomIndex];
        array[randomIndex] = temporaryValue;
      }
      return array;
    },
  },
  async mounted() {
    this.shuffledIndices = this.shuffle(this.game.backstories.map((_, index) => index));
    await this.show();
  },
};
</script>

<style lang="scss" scoped>
.backstory-view {
  position: absolute;
  bottom: 255px;
  left: 385px;
  width: 1090px;
  max-height: 560px;
  background-color: white;
  padding: 0 10px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  transition: height 0.2s ease-in-out;
}
.backstory-view h1 {
  color: black;
  font-size: 36px;
  text-align: center;
  padding: 5px;
  margin: 3px;
}
.backstory-view .backstory-description {
  padding: 20px 25px;
  color: black;
  font-size: 24px;
  line-height: 28px;
  text-align: left;
  overflow: auto;
  min-height: 100px;
  max-height: 500px;
}
.select {
  min-width: 40px;
  width: 40px;
  height: 105px;
  margin: 5px;

  img {
    height: 30%;
    width: 93%;
    // object-fit: contain;
  }
}
#left-select:focus {
  outline: none;
}
#right-select {
  img {
    transform: rotate(180deg);
  }
}
#right-select:focus {
  outline: none;
}
.fadeOutRight,
.fadeOutLeft {
  -webkit-animation-duration: 0.5s;
  -moz-animation-duration: 0.5s;
  -o-animation-duration: 0.5s;
  animation-duration: 0.5s;
}
.fadeIn {
  -webkit-animation-duration: 0.8s;
  -moz-animation-duration: 0.8s;
  -o-animation-duration: 0.8s;
  animation-duration: 0.8s;
}

.backstory-prompt-container {
  position: absolute;
  bottom: 30px;
  left: 75px;
  width: 1450px;
  min-height: 120px;
  background-color: white;
  border-radius: 1px;
  overflow: hidden;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  padding: 20px 35px;
  margin: 0;
}
.backstory-prompt {
  font-size: 24px;
  line-height: 28px;
  text-align: left;
  width: 60%;
  margin-left: 265px;
}
.backstory-submit-button {
  font-size: 32px;
  height: 60px;
  width: 215px;
}
</style>



// WEBPACK FOOTER //
// src/components/game/pages/BackstorySelect.vue